// External dependencies
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

// Internal dependencies
import { geocode } from '@/modules/core/client/utils/geocode';
import { go } from '@/modules/core/client/services/angular-compat';
import { userType } from '@/modules/users/client/users.prop-types';

const SUGGESTIONS_MIN_LENGTH = 3;
const SUGGESTIONS_DELAY_MS = 300;
const SUGGESTIONS_LIMIT = 5;
const MAX_GUESTS = 99;

/**
 * Local calendar date as `YYYY-MM-DD`
 */
export function toIsoDate(date) {
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}`;
}

/**
 * Add days to a `YYYY-MM-DD` date
 */
export function addDays(isoDate, days) {
  const [year, month, day] = isoDate.split('-').map(Number);
  return toIsoDate(new Date(year, month - 1, day + days));
}

/**
 * Place suggestions from the same geocoder the search page uses: Mapbox
 * when a key is configured, otherwise Photon (OpenStreetMap). Resolves to
 * an empty list when the request fails, so the form keeps working as a
 * plain text search.
 */
export async function fetchPlaceSuggestions(query) {
  const results = await geocode(query, { limit: SUGGESTIONS_LIMIT });
  return results.map(({ id, trTitle }) => ({ id, title: trTitle }));
}

/**
 * Turn the form values into search page URL parameters.
 * Dates travel only as a pair; guests only when a sensible number.
 */
export function buildSearchParams({ location, checkIn, checkOut, guests }) {
  const params = { location: location.trim() };

  if (checkIn && checkOut) {
    params.checkIn = checkIn;
    params.checkOut = checkOut;
  }

  const guestCount = parseInt(guests, 10);
  if (guestCount > 0 && guestCount <= MAX_GUESTS) {
    params.guests = guestCount;
  }

  return params;
}

/**
 * Home page search bar: destination, dates and guests.
 *
 * Submitting goes to the members-only search map. Visitors who are not
 * signed in are sent to the sign-in page first and continue to this search
 * afterwards, so host profiles are never shown publicly.
 */
export default function HomeSearch({ user }) {
  const { t } = useTranslation('pages');
  const today = toIsoDate(new Date());

  const [location, setLocation] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(1);

  const [suggestions, setSuggestions] = useState([]);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  // Set when a suggestion is picked so we don't re-query for its own title
  const skipNextLookup = useRef(false);
  const locationInput = useRef(null);

  // Look up place suggestions as the destination is typed
  useEffect(() => {
    if (skipNextLookup.current) {
      skipNextLookup.current = false;
      return;
    }

    const query = location.trim();
    if (query.length < SUGGESTIONS_MIN_LENGTH) {
      setSuggestions([]);
      setIsSuggestionsOpen(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      const results = await fetchPlaceSuggestions(query);
      if (!cancelled) {
        setSuggestions(results);
        setIsSuggestionsOpen(results.length > 0);
        setActiveSuggestion(-1);
      }
    }, SUGGESTIONS_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [location]);

  function pickSuggestion(suggestion) {
    skipNextLookup.current = true;
    setLocation(suggestion.title);
    setSuggestions([]);
    setIsSuggestionsOpen(false);
    setActiveSuggestion(-1);
  }

  function onLocationKeyDown(event) {
    if (!isSuggestionsOpen) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveSuggestion(index =>
          index < suggestions.length - 1 ? index + 1 : 0,
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveSuggestion(index =>
          index > 0 ? index - 1 : suggestions.length - 1,
        );
        break;
      case 'Enter':
        if (activeSuggestion >= 0) {
          event.preventDefault();
          pickSuggestion(suggestions[activeSuggestion]);
        }
        break;
      case 'Escape':
        setIsSuggestionsOpen(false);
        break;
      default:
    }
  }

  function onCheckInChange({ target: { value } }) {
    setCheckIn(value);
    // Keep at least one night between the dates
    if (value && checkOut && checkOut <= value) {
      setCheckOut(addDays(value, 1));
    }
  }

  function onCheckOutChange({ target: { value } }) {
    if (value && checkIn && value <= checkIn) {
      setCheckOut(addDays(checkIn, 1));
    } else {
      setCheckOut(value);
    }
  }

  function onSubmit(event) {
    event.preventDefault();

    if (!location.trim()) {
      locationInput.current?.focus();
      return;
    }

    setIsSuggestionsOpen(false);
    go(
      'search.map',
      buildSearchParams({ location, checkIn, checkOut, guests }),
    );
  }

  const suggestionsId = 'home-search-suggestions';

  return (
    <div className="home-search-wrapper">
      <form
        className="home-search"
        role="search"
        aria-label={t('Search hosts')}
        onSubmit={onSubmit}
        noValidate
      >
        <div className="home-search-field home-search-location">
          <label htmlFor="home-search-location">{t('Destination')}</label>
          <input
            ref={locationInput}
            id="home-search-location"
            type="text"
            className="form-control"
            placeholder={t('Where do you want to go?')}
            value={location}
            autoComplete="off"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={isSuggestionsOpen}
            aria-controls={suggestionsId}
            aria-activedescendant={
              activeSuggestion >= 0
                ? `${suggestionsId}-${activeSuggestion}`
                : undefined
            }
            onChange={({ target: { value } }) => setLocation(value)}
            onKeyDown={onLocationKeyDown}
            onFocus={() => suggestions.length && setIsSuggestionsOpen(true)}
            onBlur={() => setIsSuggestionsOpen(false)}
          />
          {isSuggestionsOpen && (
            <ul
              id={suggestionsId}
              className="home-search-suggestions"
              role="listbox"
            >
              {suggestions.map((suggestion, index) => (
                <li
                  key={suggestion.id}
                  id={`${suggestionsId}-${index}`}
                  role="option"
                  aria-selected={index === activeSuggestion}
                  className={classnames({
                    'is-active': index === activeSuggestion,
                  })}
                  // `onMouseDown` fires before the input's blur closes the list
                  onMouseDown={event => {
                    event.preventDefault();
                    pickSuggestion(suggestion);
                  }}
                  onMouseEnter={() => setActiveSuggestion(index)}
                >
                  {suggestion.title}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="home-search-field">
          <label htmlFor="home-search-check-in">{t('Check-in')}</label>
          <input
            id="home-search-check-in"
            type="date"
            className="form-control"
            min={today}
            value={checkIn}
            onChange={onCheckInChange}
          />
        </div>

        <div className="home-search-field">
          <label htmlFor="home-search-check-out">{t('Check-out')}</label>
          <input
            id="home-search-check-out"
            type="date"
            className="form-control"
            min={checkIn ? addDays(checkIn, 1) : addDays(today, 1)}
            value={checkOut}
            onChange={onCheckOutChange}
          />
        </div>

        <div className="home-search-field home-search-guests">
          <label htmlFor="home-search-guests">{t('Guests')}</label>
          <input
            id="home-search-guests"
            type="number"
            className="form-control"
            min="1"
            max={MAX_GUESTS}
            step="1"
            value={guests}
            onChange={({ target: { value } }) => setGuests(value)}
          />
        </div>

        <button type="submit" className="btn btn-primary home-search-submit">
          <i className="icon-search" aria-hidden="true"></i> {t('Search')}
        </button>
      </form>

      {!user && (
        <p className="home-search-note">
          {t(
            'Host profiles are private: sign in or join to see hosts and send a booking request.',
          )}
        </p>
      )}
    </div>
  );
}

HomeSearch.propTypes = {
  user: userType,
};
